import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
  CardTitle,
  Row,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import { getActiveColorStyle } from '../../utils/colorUtils';

import {
  FOOTER_BUTTONS,
  HELP_MENU_ITEMS,
  MFA_GUIDES,
  BUTTON_STYLES,
  DRAWER_STYLES
} from './constants';
import {
  FooterButton,
  HelpMenu,
  MfaGuideDrawer
} from './components';
import { useTranslation } from 'react-i18next';
import { IoGrid } from "react-icons/io5";
import { MdDashboard, MdSave, MdBlock, MdDelete, MdRemoveCircleOutline, MdViewQuilt, MdPrint, MdUndo } from "react-icons/md";
import { useGetAccount } from "api/account/service";

/**
 * Footer component that provides floating action buttons for navigation and help
 */
const Footer = ({ activeColor = "secondary", isLightTheme }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isAccountDetail = useMemo(() => /^\/(?:admin\/)?accounts\/[^/]+\/?$/.test(location.pathname), [location.pathname]);
  const isReportAdd = useMemo(() => /^\/(?:admin\/)?reports\/[^/]+\/?$/.test(location.pathname), [location.pathname]);
  const isCustomerDetail = useMemo(() => /^\/(?:admin\/)?customers\/[^/]+\/?$/.test(location.pathname), [location.pathname]);
  const isDealsDetail = useMemo(() => /^\/(?:admin\/)?deals\/[^/]+\/?$/.test(location.pathname), [location.pathname]);
  const isInventoryDetail = useMemo(() => /^\/(?:admin\/)?inventory\/[^/]+\/?$/.test(location.pathname), [location.pathname]);
  const isChannelDetail = useMemo(() => /^\/(?:admin\/)?channels\/(?!add)[^/]+\/?$/.test(location.pathname), [location.pathname]);
  const isCommunicationTemplateDetail = useMemo(() => /^\/(?:admin\/)?communication-templates\/(?!add)[^/]+\/?$/.test(location.pathname), [location.pathname]);
  const isWebMaker = useMemo(() => /^\/(?:admin\/)?web-maker\/?$/.test(location.pathname), [location.pathname]);

  // Consolidated state management
  const [drawerStates, setDrawerStates] = useState({
    howTo: false,
    mfa: false,
    dealership: false,
    disabled: false,
    lostDevice: false,
    loginIssue: false,
    noEmail: false,
    chat: false
  });

  const [menuStates, setMenuStates] = useState({
    help: false,
    trainingHover: false,
    webMakerTheme: false,
  });
  const [channelActionMode, setChannelActionMode] = useState('delete');

  const { data: account } = useGetAccount();
  const canReadAnalytics = account?.permissions?.dashboard_read_analytics === true;
  const canDeleteInventory = account?.permissions?.inventory_delete === true;
  const canDeleteChannels = account?.permissions?.channels_delete === true;

  const isDashboard = useMemo(() => location.pathname.includes("dashboard"), [location.pathname]);

  const [dashboardMode, setDashboardMode] = useState(() => localStorage.getItem('dashboardViewType') || 'analytics');

  useEffect(() => {
    const handleSync = () => {
      setDashboardMode(localStorage.getItem('dashboardViewType') || 'analytics');
    };
    window.addEventListener('dashboard-view-changed', handleSync);
    return () => window.removeEventListener('dashboard-view-changed', handleSync);
  }, []);

  const toggleDashboardMode = useCallback(() => {
    if (!canReadAnalytics) return;
    const newMode = dashboardMode === 'analytics' ? 'quick-menu' : 'analytics';
    setDashboardMode(newMode);
    localStorage.setItem('dashboardViewType', newMode);
    window.dispatchEvent(new Event('dashboard-view-changed'));
  }, [dashboardMode, canReadAnalytics]);

  const [closeMenu, setCloseMenu] = useState(false);
  const [showSettingsActions, setShowSettingsActions] = useState(false);
  const [hoverStates, setHoverStates] = useState({
    button: null,
    text: null
  });

  // Event handlers
  const handleMailClick = useCallback(() => {
    // navigate('/footer/message-center');
  }, [navigate]);

  const handleDrawerToggle = useCallback((drawerName, isOpen) => {
    setDrawerStates(prev => ({
      ...prev,
      [drawerName]: isOpen
    }));
  }, []);

  useEffect(() => {
    window.openMfaHowToModal = () => handleDrawerToggle('howTo', true);
    window.closeMfaHowToModal = () => handleDrawerToggle('howTo', false);

    return () => {
      delete window.openMfaHowToModal;
      delete window.closeMfaHowToModal;
    };
  }, [handleDrawerToggle]);

  const handleMenuToggle = useCallback((menuName, isOpen) => {
    setMenuStates(prev => ({
      ...prev,
      [menuName]: isOpen
    }));
  }, []);


  useEffect(() => {
    window.dispatchEvent(new CustomEvent('menu-settings-mode', { detail: showSettingsActions }));
  }, [showSettingsActions]);

  useEffect(() => {
    const handleThemePickerState = (event) => {
      setMenuStates((prev) => ({
        ...prev,
        webMakerTheme: Boolean(event.detail?.open),
      }));
    };

    window.addEventListener("web-maker-theme-picker-state", handleThemePickerState);
    return () => window.removeEventListener("web-maker-theme-picker-state", handleThemePickerState);
  }, []);

  useEffect(() => {
    const handleChannelActionMode = (event) => {
      setChannelActionMode(event.detail === 'restore' ? 'restore' : 'delete');
    };

    if (!isChannelDetail) {
      setChannelActionMode('delete');
      return undefined;
    }

    window.addEventListener('channel-detail-action-mode', handleChannelActionMode);
    return () => window.removeEventListener('channel-detail-action-mode', handleChannelActionMode);
  }, [isChannelDetail]);

  const handleHoverChange = useCallback((type, value) => {
    setHoverStates(prev => ({
      ...prev,
      [type]: value
    }));
  }, []);

  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  // Utility functions
  const getIconColor = useCallback(() => {
    // Default to dark theme colors
    return '#fff';
  }, []);

  const getMailIconColor = useCallback(() => getIconColor(), [getIconColor]);

  const getButtonStyle = useCallback((buttonId) => {
    const isHovered = hoverStates.button === buttonId;
    const isActive = buttonId === 'toggle' && isDashboard;

    const baseStyle = {
      ...BUTTON_STYLES.base,
      backgroundColor: '#1d212c',
      borderColor: isActive ? activeColorStyle.borderColor : 'rgba(255, 255, 255, 0.3)',
      borderWidth: isActive ? '2px' : '1px',
      borderStyle: 'solid',
      marginLeft: '4px',
      transform: 'scale(1)',
      boxShadow: isActive ? `0 0 15px ${activeColorStyle.shadow}` : 'none',
      filter: 'none',
      zIndex: isActive ? 10 : 'auto',
      transition: 'all 0.3s ease'
    };

    const hoverStyle = isHovered ? {
      ...BUTTON_STYLES.hovered,
      backgroundColor: isActive ? activeColorStyle.background : activeColorStyle.background,
      borderColor: activeColorStyle.borderColor,
      borderWidth: '2px',
      transform: 'scale(1.15)',
      boxShadow: `0 8px 25px rgba(0, 0, 0, 0.5), 0 0 15px ${activeColorStyle.shadow}`,
      filter: 'brightness(1.2) contrast(1.1)',
      zIndex: 1000
    } : {};

    return {
      ...baseStyle,
      ...hoverStyle
    };
  }, [hoverStates.button, activeColor, activeColorStyle, isDashboard]);

  return (
    <>
      {/* Floating Action Buttons */}
      {!isAccountDetail && !isReportAdd && !isCustomerDetail && !isDealsDetail &&
        <div style={DRAWER_STYLES.floatingContainer}>
          {!closeMenu && (
            <>
              {isWebMaker && (
                <FooterButton
                  id="web-maker-theme"
                  data-web-maker-theme-trigger="true"
                  title="Select Theme"
                  icon={<i className="tim-icons icon-palette" />}
                  onClick={() => window.dispatchEvent(new CustomEvent("web-maker-theme-picker-toggle"))}
                  onHoverChange={(isHovered) =>
                    handleHoverChange("button", isHovered ? "web-maker-theme" : null)
                  }
                  style={{
                    ...getButtonStyle("web-maker-theme"),
                    ...(menuStates.webMakerTheme
                      ? {
                          borderColor: activeColorStyle.borderColor,
                          borderWidth: "2px",
                          boxShadow: `0 0 15px ${activeColorStyle.shadow}`,
                        }
                      : {}),
                  }}
                  iconColor={getIconColor()}
                />
              )}
              {/* <FooterButton
                id="chat"
                icon={<p style={{ color: 'white', margin: 0, fontSize: '12px', fontWeight: 'bold' }}>AI</p>}
                onClick={() => handleDrawerToggle('chat', !drawerStates.chat)}
                onHoverChange={(isHovered) => handleHoverChange('button', isHovered ? 'chat' : null)}
                style={getButtonStyle('chat')}
                iconColor={getIconColor()}
              /> */}
              {/* <FooterButton
                id="mail"
                icon={
                  <i className={`tim-icons icon-email-85`} style={{ color: getMailIconColor() }} />
                }
                onClick={handleMailClick}
                onHoverChange={(isHovered) => handleHoverChange('button', isHovered ? 'mail' : null)}
                style={getButtonStyle('mail')}
                iconColor={getMailIconColor()}
              /> */}
              <HelpMenu
                isOpen={menuStates.help}
                onToggle={(isOpen) => handleMenuToggle('help', isOpen)}
                onHoverChange={(isHovered) => handleHoverChange('button', isHovered ? 'help' : null)}
                style={getButtonStyle('help')}
                iconColor={getIconColor()}
                trainingHover={menuStates.trainingHover}
                onTrainingHoverChange={(isHovered) => handleMenuToggle('trainingHover', isHovered)}
                activeColor={activeColor}
                isLightTheme={isLightTheme}
              />
          
              {!isInventoryDetail && !isDashboard && (
                <FooterButton
                  id="school"
                  icon={<i className="tim-icons icon-bulb-63" />}
                  onClick={() => handleDrawerToggle('howTo', true)}
                  onHoverChange={(isHovered) => handleHoverChange('button', isHovered ? 'school' : null)}
                  style={getButtonStyle('school')}
                  iconColor={getIconColor()}
                />
              )}
              {isDashboard && dashboardMode === 'quick-menu' && (
                <FooterButton
                  id="quick-settings"
                  icon={<i className="tim-icons icon-settings-gear-63" />}
                  onClick={() => {
                    setShowSettingsActions(!showSettingsActions);
                  }}
                  onHoverChange={(isHovered) => handleHoverChange('button', isHovered ? 'quick-settings' : null)}
                  style={getButtonStyle('quick-settings')}
                  iconColor={getIconColor()}
                />
              )}
              {isDashboard && canReadAnalytics && (
                <FooterButton
                  id="toggle-dashboard"
                  icon={dashboardMode === 'analytics' ? <IoGrid size={20} /> : <MdDashboard size={20} />}
                  onClick={toggleDashboardMode}
                  onHoverChange={(isHovered) => handleHoverChange('button', isHovered ? 'toggle' : null)}
                  style={getButtonStyle('toggle')}
                  iconColor={getIconColor()}
                />
              )}
              {isInventoryDetail && (
                <FooterButton
                  id="print-inventory"
                  icon={<MdPrint size={20} />}
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('print-inventory-requested'));
                  }}
                  onHoverChange={(isHovered) => handleHoverChange('button', isHovered ? 'print-inventory' : null)}
                  style={getButtonStyle('print-inventory')}
                  iconColor={getIconColor()}
                />
              )}
              {((isInventoryDetail && canDeleteInventory) || (isChannelDetail && canDeleteChannels) || isCommunicationTemplateDetail) && (
                <>
                  <style>{`
                    .btn-remove-inventory, 
                    .btn-remove-inventory:focus, 
                    .btn-remove-inventory:active {
                      background: linear-gradient(135deg, #fd5d93, #ec250d, #fd5d93) !important;
                      background-image: linear-gradient(135deg, #fd5d93, #ec250d, #fd5d93) !important;
                      border-color: #fd5d93 !important;
                      color: white !important;
                    }
                    .btn-remove-inventory:hover {
                      box-shadow: 0 8px 15px rgba(0, 0, 0, 0.3) !important;
                    }
                    .btn-restore-channel,
                    .btn-restore-channel:focus,
                    .btn-restore-channel:active {
                      background: linear-gradient(135deg, #00bf72, #00a651, #00bf72) !important;
                      background-image: linear-gradient(135deg, #00bf72, #00a651, #00bf72) !important;
                      border-color: #00a651 !important;
                      color: white !important;
                    }
                    .btn-restore-channel:hover {
                      box-shadow: 0 8px 15px rgba(0, 166, 81, 0.35) !important;
                    }
                  `}</style>
                  <FooterButton
                    id="remove-item"
                    className={isChannelDetail && channelActionMode === 'restore' ? "btn-restore-channel" : "btn-remove-inventory"}
                    title={isChannelDetail && channelActionMode === 'restore' ? t("channels.restoreChannel") : t("common.remove")}
                    icon={
                      isChannelDetail && channelActionMode === 'restore'
                        ? <MdUndo size={18} />
                        : <MdDelete size={18} />
                    }
                    onClick={() => {
                      const eventName = isInventoryDetail
                        ? 'delete-inventory-requested'
                        : isChannelDetail
                          ? 'delete-channel-requested'
                          : 'delete-template-requested';
                      window.dispatchEvent(new CustomEvent(eventName));
                    }}
                    onHoverChange={(isHovered) => handleHoverChange('button', isHovered ? 'remove-item' : null)}
                    style={{
                      backgroundColor:
                        isChannelDetail && channelActionMode === 'restore' ? '#00a651' : undefined,
                      borderColor:
                        isChannelDetail && channelActionMode === 'restore' ? '#00a651' : undefined,
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      marginLeft: '4px',
                      transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      ...(hoverStates.button === 'remove-item' ? {
                        transform: 'scale(1.25)',
                        zIndex: 1000
                      } : {})
                    }}
                    iconColor="#fff"
                  />
                </>
              )}
            </>
          )}
          <div style={{ position: 'absolute', bottom: 0, right: '100%', paddingRight: '6px', display: 'flex', alignItems: 'center' }}>

            {showSettingsActions && (
              <div style={{ display: 'flex', paddingRight: '8px' }}>
                <Button
                  size="sm"
                  style={{ margin: 0, height: 36, width: 40, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 0, backgroundColor: '#181b23', border: '1px solid #fff', borderRight: '1px solid rgba(255,255,255,0.2)', color: 'white' }}
                  title="Default"
                  onClick={() => {
                    window.dispatchEvent(new Event('reset-menu-settings'));
                    setShowSettingsActions(false);
                  }}
                >
                  <MdRemoveCircleOutline size={22} />
                </Button>
                <Button
                  size="sm"
                  style={{ margin: 0, height: 36, width: 40, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 0, backgroundColor: '#181b23', borderTop: '1px solid #fff', borderBottom: '1px solid #fff', borderLeft: 'none', borderRight: '1px solid rgba(255,255,255,0.2)', color: 'white' }}
                  onClick={() => {
                    setShowSettingsActions(false);
                    window.dispatchEvent(new Event('cancel-menu-settings'));
                  }}
                  title="Cancel"
                >
                  <MdBlock size={22} />
                </Button>
                <Button
                  size="sm"
                  style={{ margin: 0, height: 36, width: 40, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 0, backgroundColor: '#13a829', backgroundImage: 'none', border: '1px solid #fff', borderLeft: 'none', color: 'white' }}
                  title="Save"
                  onClick={() => {
                    window.dispatchEvent(new Event('save-menu-settings'));
                    setShowSettingsActions(false);
                  }}
                >
                  <MdSave size={22} />
                </Button>
              </div>
            )}
          </div>

          <FooterButton
            id="back"
            icon={<i className={`tim-icons  ${closeMenu ? 'icon-minimal-up' : 'icon-minimal-down'}`} />}
            onHoverChange={(isHovered) => handleHoverChange('button', isHovered ? 'back' : null)}
            onClick={() => setCloseMenu(!closeMenu)}
            style={getButtonStyle('back')}
            iconColor={getIconColor()}
          />
        </div>
      }

      {/* MFA How-To Modal */}
      <Modal
        isOpen={drawerStates.howTo}
        toggle={() => handleDrawerToggle('howTo', false)}
        size="lg"
        style={{
          marginTop: '80px'
        }}
      >
        <ModalHeader toggle={() => handleDrawerToggle('howTo', false)}>
          MFA How-To's
        </ModalHeader>
        <ModalBody>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {MFA_GUIDES.map((guide) => (
              <div
                key={guide.id}
                style={{
                  cursor: 'pointer',
                  color: hoverStates.text === guide.id ? '#3b82f6' : undefined,
                  padding: '8px 0'
                }}
                onClick={() => handleDrawerToggle(guide.drawerKey, true)}
                onMouseEnter={() => handleHoverChange('text', guide.id)}
                onMouseLeave={() => handleHoverChange('text', null)}
              >
                {guide.title}
              </div>
            ))}
          </div>
        </ModalBody>
      </Modal>

      {/* MFA Guide Modals */}
      {MFA_GUIDES.map((guide) => (
        <MfaGuideDrawer
          key={guide.id}
          guide={guide}
          isOpen={drawerStates[guide.drawerKey]}
          onClose={() => handleDrawerToggle(guide.drawerKey, false)}
          zIndex={1000 + guide.zIndexOffset}
        />
      ))}


    </>
  );
};

export default Footer; 